import { LitElement, html, css } from 'lit';
import { API_BASE_URL } from './utils.js';

export class ChangeDetectorLogsTab extends LitElement {
    static properties = {
        contextManager: { type: Object },
        queryParams: { type: Object },
        activeContextId: { type: String },
        applicationName: { type: String },
        activationId: { type: String },
        activationData: { type: String },
        activationDetails: { type: String },
        isLoading: { type: Boolean },
        prismLoaded: { state: true },
        _isCopied: { state: true },
    };

    static styles = css`
        :host {
            display: block;
            padding: 20px;
        }
        .header-container {
            display: flex;
            align-items: center;
            gap: 8px;
            cursor: pointer;
        }
        .header-container h3 {
            margin: 0;
        }
        .copied-feedback {
            font-style: italic;
            color: var(--spectrum-global-color-gray-700);
        }
        .container {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }
        .input-row {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            align-items: flex-end;
        }
        .input-row sp-textfield {
            flex: 1 1 250px; /* Allow fields to grow and shrink, with a base width */
        }
        .input-row sp-button {
            flex-shrink: 0; /* Prevent the button from shrinking */
        }
        .logs-output {
            color: #ffffff; /* Make text visible on dark background */
            background-color: #2d2d2d;
            padding: 16px;
            border-radius: 4px;
            border: 1px solid #444;
        }
        .logs-output pre, .log-line {
            text-align: left;
            width: 100%;
            white-space: pre-wrap;
            word-break: break-word;
            font-family: monospace;
        }
        .log-line.error {
            color: #ff9b9b;
            background-color: rgba(255, 0, 0, 0.15);
        }
        .logs-output pre {
            margin: 0;
            white-space: pre-wrap;      /* Ensures content wraps */
            word-break: break-all;      /* Breaks long strings */
        }
        .loading-hidden {
            display: none;
        }
    `;

    constructor() {
        super();
        this.contextManager = null;
        this.queryParams = {};
        this.activeContextId = null;
        this.applicationName = '';
        this.activationId = '';
        this.activationData = '';
        this.activationDetails = '';
        this.isLoading = false;
        this.prismLoaded = false;
        this._isCopied = false;
    }

    renderLogLines() {
        if (!this.activationData) {
            return html`<pre></pre>`;
        }

        let parsedData;
        try {
            parsedData = JSON.parse(this.activationData);
        } catch (e) {
            return html`<pre>${this.activationData}</pre>`;
        }

        const logLines = parsedData?.logs?.logs;

        if (Array.isArray(logLines)) {
            if (logLines.length === 0) {
                return html`<div>No log lines found in activation.</div>`;
            }
            return html`
                ${logLines.map(line => {
                    const isError = line.toLowerCase().includes('error:') || line.toLowerCase().includes('stderr:');
                    const skuMatch = line.match(/Error generating product HTML for SKU ([\w-]+)/);

                    if (skuMatch) {
                        const sku = skuMatch[1];
                        const link = `/#/product-catalog?sku=${sku}`;
                        const parts = line.split(sku);
                        
                        return html`
                            <div class="log-line ${isError ? 'error' : ''}">
                                ${parts[0]}<a href=${link}>${sku}</a>${parts[1]}
                            </div>`;
                    }

                    return html`<div class="log-line ${isError ? 'error' : ''}">${line}</div>`;
                })}
            `;
        }
        
        return html`<pre>${this.activationData}</pre>`;
    }

    updated(changedProperties) {
        if (changedProperties.has('queryParams')) {
            this.processQueryParams();
        }
        if (changedProperties.has('activeContextId') && this.activeContextId) {
            this.applicationName = this.activeContextId;
        }
    }

    processQueryParams() {
        const appName = this.queryParams.applicationName || '';
        const activationId = this.queryParams.activationId || '';
        
        this.applicationName = appName;
        this.activationId = activationId;

        if (!appName) {
            this.activationData = '';
            return;
        }

        if (appName.includes('/')) {
            this.handleContextualLogRequest(appName, activationId);
        } else if (activationId) {
            this.fetchLogs();
            this.fetchActivationDetails();
        } else {
            this.activationData = '';
        }
    }

    handleContextualLogRequest(appName, activationId) {
        const potentialContextId = decodeURIComponent(appName);
        
        const contextExists = this.contextManager.contexts.some(ctx => ctx.id === potentialContextId);

        if (contextExists) {
            if (this.contextManager.activeContextId !== potentialContextId) {
                this.dispatchEvent(new CustomEvent('set-active-context', {
                    bubbles: true,
                    composed: true,
                    detail: { contextId: potentialContextId }
                }));
            }
            
            if (activationId) {
                this.fetchLogs();
                this.fetchActivationDetails();
            } else {
                this.activationData = 'Activation ID is missing.';
                this.activationDetails = '';
            }
        } else {
            this.activationData = `Context '${potentialContextId}' not found in localStorage.`;
        }
    }

    async fetchLogs() {
        if (!this.applicationName) {
            this.activationData = 'Application Name is required to fetch logs.';
            return;
        }
        if (!this.activationId) {
            this.activationData = 'Activation ID is required to fetch logs.';
            return;
        }

        this.isLoading = true;
        const headers = {};

        if (this.applicationName && this.activationId && this.contextManager) {
            const credentials = this.contextManager.getContextCredentials();
            if (credentials && credentials.aioAuth && credentials.aioNamespace) {
                headers['X-AIO-auth'] = credentials.aioAuth;
                headers['X-AIO-namespace'] = credentials.aioNamespace;
            }
        }

        try {
             const response = await fetch(`${API_BASE_URL}/logs?activationId=${this.activationId}&applicationName=${encodeURIComponent(this.applicationName)}`, {
                headers,
             });
            
            if (response.ok) {
                const data = await response.json();
                this.activationData = JSON.stringify(data, null, 2);
            } else {
                const errorText = await response.text();
                this.activationData = `Error fetching logs: ${response.status} ${response.statusText}\n${errorText}`;
            }
        } catch (error) {
            this.activationData = `Error fetching logs: ${error}`;
        } finally {
            this.isLoading = false;
        }
    }

    async fetchActivationDetails() {
        if (!this.activationId) {
            this.activationDetails = 'Activation ID is required to fetch details.';
            return;
        }

        this.isLoading = true;
        const headers = {};

        if (this.contextManager) {
            const credentials = this.contextManager.getContextCredentials();
            if (credentials && credentials.aioAuth && credentials.aioNamespace) {
                headers['X-AIO-auth'] = credentials.aioAuth;
                headers['X-AIO-namespace'] = credentials.aioNamespace;
            }
        }

        try {
            const url = `${API_BASE_URL}/activation?activationId=${this.activationId}`;
            const response = await fetch(url, { headers });

            if (response.ok) {
                const data = await response.json();
                this.activationDetails = JSON.stringify(data, null, 2);
            } else {
                const errorText = await response.text();
                this.activationDetails = `Error fetching activation details: ${response.status} ${response.statusText}\n${errorText}`;
            }
        } catch (error) {
            this.activationDetails = `Error fetching activation details: ${error}`;
        } finally {
            this.isLoading = false;
        }
    }

    handleGetLogs(e) {
        e.preventDefault();
        e.stopPropagation();

        const params = new URLSearchParams();
        if (this.applicationName) {
            params.set('applicationName', this.applicationName);
        }
        if (this.activationId) {
            params.set('activationId', this.activationId);
        }
        const queryString = params.toString();
        window.location.hash = `/#/change-detector-logs${queryString ? `?${queryString}` : ''}`;
    }
    
    handleApplicationNameInput(e) {
        this.applicationName = e.target.value;
    }

    handleActivationIdInput(e) {
        this.activationId = e.target.value;
    }

    _handleHeaderClick() {
        navigator.clipboard.writeText(window.location.href);
        this._isCopied = true;
        setTimeout(() => {
            this._isCopied = false;
        }, 2000);
    }

    render() {
        return html`
            <div class="container">
                <div class="header-container" 
                    @click=${this._handleHeaderClick}>
                    <h3>Change Detector Logs</h3>
                    ${this._isCopied
                        ? html`<span class="copied-feedback">Copied!</span>`
                        : html`<span class="clipboard-emoji">📋</span>`
                    }
                </div>
                <div class="input-row">
                    <sp-textfield
                        label="Application Name"
                        .value=${this.applicationName}
                        @input=${this.handleApplicationNameInput}
                    ></sp-textfield>
                    <sp-textfield
                        label="Activation ID"
                        .value=${this.activationId}
                        @input=${this.handleActivationIdInput}
                    ></sp-textfield>
                    <sp-button @click=${this.handleGetLogs} .disabled=${this.isLoading}>Get Logs</sp-button>
                </div>
                <sp-progress-circle .indeterminate=${this.isLoading} label="Loading logs..." class="${this.isLoading ? '' : 'loading-hidden'}"></sp-progress-circle>
                
                <h3>Logs</h3>
                <div class="logs-output">
                     ${this.renderLogLines()}
                </div>

                <h3>Activation Details</h3>
                <div class="logs-output">
                    <pre>${this.activationDetails}</pre>
                </div>
            </div>
        `;
    }
}

customElements.define('change-detector-logs-tab', ChangeDetectorLogsTab); 